/**
 * Evaluation Reports Page
 * Dashboard for PCW and RN performance evaluation analytics
 */
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Star,
  Clock,
  FileCheck,
  AlertTriangle,
  RefreshCw,
  Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EvaluationReportsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [timeRange, setTimeRange] = useState("year");
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    acknowledged: 0,
    averageRating: 0,
    byEmployee: {},
    byMonth: {},
    trends: []
  });

  useEffect(() => {
    fetchData();
  }, [selectedEmployee, selectedType, timeRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch evaluations
      const [pcwRes, rnRes, empRes] = await Promise.all([
        axios.get(`${API}/api/hr/pcw-evaluations`, { headers }),
        axios.get(`${API}/api/hr/rn-evaluations`, { headers }),
        axios.get(`${API}/api/employees`, { headers })
      ]);

      const allEvaluations = [
        ...pcwRes.data.map(e => ({ ...e, eval_type: "pcw" })),
        ...rnRes.data.map(e => ({ ...e, eval_type: "rn" }))
      ];

      setEmployees(empRes.data);
      
      // Filter based on selections
      let filtered = allEvaluations;
      
      if (selectedEmployee !== "all") {
        filtered = filtered.filter(e => e.employee_id === selectedEmployee);
      }
      
      if (selectedType !== "all") {
        filtered = filtered.filter(e => e.eval_type === selectedType);
      }

      // Calculate time range
      const now = new Date();
      let startDate;
      switch (timeRange) {
        case "month":
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          break;
        case "quarter":
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        case "year":
        default:
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      }

      filtered = filtered.filter(e => {
        const evalDate = new Date(e.evaluation_date || e.created_at);
        return evalDate >= startDate;
      });

      setEvaluations(filtered);
      calculateStats(filtered, empRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load evaluation data");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (evals, emps) => {
    const total = evals.length;
    const pending = evals.filter(e => e.status === "pending" || e.status === "sent_for_signature").length;
    const acknowledged = evals.filter(e => e.status === "acknowledged").length;
    
    const ratings = evals.map(e => e.average_rating).filter(r => r > 0);
    const averageRating = ratings.length > 0 
      ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(2)
      : 0;

    // Group by employee
    const byEmployee = {};
    evals.forEach(e => {
      if (!byEmployee[e.employee_id]) {
        const emp = emps.find(emp => emp.id === e.employee_id);
        byEmployee[e.employee_id] = {
          name: e.employee_name || emp?.name || "Unknown",
          count: 0,
          totalRating: 0,
          ratings: []
        };
      }
      byEmployee[e.employee_id].count++;
      if (e.average_rating > 0) {
        byEmployee[e.employee_id].totalRating += e.average_rating;
        byEmployee[e.employee_id].ratings.push(e.average_rating);
      }
    });

    // Calculate averages
    Object.keys(byEmployee).forEach(id => {
      const data = byEmployee[id];
      data.averageRating = data.ratings.length > 0
        ? (data.totalRating / data.ratings.length).toFixed(2)
        : 0;
      
      // Calculate trend
      if (data.ratings.length >= 2) {
        const lastTwo = data.ratings.slice(-2);
        data.trend = lastTwo[1] > lastTwo[0] ? "up" : lastTwo[1] < lastTwo[0] ? "down" : "stable";
      } else {
        data.trend = "stable";
      }
    });

    // Group by month
    const byMonth = {};
    evals.forEach(e => {
      const date = new Date(e.evaluation_date || e.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = { count: 0, totalRating: 0 };
      }
      byMonth[monthKey].count++;
      if (e.average_rating > 0) {
        byMonth[monthKey].totalRating += e.average_rating;
      }
    });

    setStats({
      total,
      pending,
      acknowledged,
      averageRating,
      byEmployee,
      byMonth
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "text-green-600";
    if (rating >= 3) return "text-yellow-600";
    return "text-red-600";
  };

  const getRatingLabel = (rating) => {
    if (rating >= 4.5) return "Exceptional";
    if (rating >= 4) return "Exceeds";
    if (rating >= 3) return "Meets";
    if (rating >= 2) return "Needs Improvement";
    return "Unsatisfactory";
  };

  if (user?.role !== "admin" && user?.role !== "rn") {
    return (
      <Layout>
        <div className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-gray-500">Only Administrators and RNs can access evaluation reports.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="evaluation-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evaluation Reports</h1>
          <p className="text-gray-500">Performance analytics for PCW and RN evaluations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-white border rounded-lg">
        <div className="flex-1 min-w-48">
          <label className="text-sm text-gray-500 mb-1 block">Employee</label>
          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
            <SelectTrigger>
              <SelectValue placeholder="All Employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-48">
          <label className="text-sm text-gray-500 mb-1 block">Evaluation Type</label>
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="pcw">PCW Evaluations</SelectItem>
              <SelectItem value="rn">RN Evaluations</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-48">
          <label className="text-sm text-gray-500 mb-1 block">Time Range</label>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Last Month</SelectItem>
              <SelectItem value="quarter">Last Quarter</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Evaluations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-teal-600" />
              <span className="text-3xl font-bold">{stats.total}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pending Signature</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="text-3xl font-bold">{stats.pending}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Acknowledged</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-green-600" />
              <span className="text-3xl font-bold">{stats.acknowledged}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Average Rating</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Star className={`w-5 h-5 ${getRatingColor(stats.averageRating)}`} />
              <span className={`text-3xl font-bold ${getRatingColor(stats.averageRating)}`}>
                {stats.averageRating}
              </span>
              <span className="text-sm text-gray-500">/ 5.0</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Employee Performance Summary
          </CardTitle>
          <CardDescription>
            Individual performance ratings and trends
          </CardDescription>
        </CardHeader>
        <CardContent>
          {Object.keys(stats.byEmployee).length === 0 ? (
            <p className="text-center text-gray-500 py-8">No evaluation data available for the selected filters.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-center">Evaluations</TableHead>
                  <TableHead className="text-center">Avg Rating</TableHead>
                  <TableHead className="text-center">Performance</TableHead>
                  <TableHead className="text-center">Trend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(stats.byEmployee)
                  .sort((a, b) => parseFloat(b[1].averageRating) - parseFloat(a[1].averageRating))
                  .map(([id, data]) => (
                    <TableRow key={id}>
                      <TableCell className="font-medium">{data.name}</TableCell>
                      <TableCell className="text-center">{data.count}</TableCell>
                      <TableCell className="text-center">
                        <span className={`font-semibold ${getRatingColor(data.averageRating)}`}>
                          {data.averageRating}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          data.averageRating >= 4 ? "bg-green-100 text-green-700" :
                          data.averageRating >= 3 ? "bg-yellow-100 text-yellow-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {getRatingLabel(data.averageRating)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {data.trend === "up" && <TrendingUp className="w-4 h-4 text-green-600 mx-auto" />}
                        {data.trend === "down" && <TrendingDown className="w-4 h-4 text-red-600 mx-auto" />}
                        {data.trend === "stable" && <span className="text-gray-400">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent Evaluations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Recent Evaluations
          </CardTitle>
          <CardDescription>
            Latest evaluation records
          </CardDescription>
        </CardHeader>
        <CardContent>
          {evaluations.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No evaluations found for the selected filters.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Evaluator</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {evaluations
                  .sort((a, b) => new Date(b.evaluation_date || b.created_at) - new Date(a.evaluation_date || a.created_at))
                  .slice(0, 20)
                  .map((evaluation) => (
                    <TableRow key={evaluation.id}>
                      <TableCell>
                        {new Date(evaluation.evaluation_date || evaluation.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{evaluation.employee_name}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${
                          evaluation.eval_type === "rn" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                        }`}>
                          {evaluation.eval_type?.toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`font-semibold ${getRatingColor(evaluation.average_rating)}`}>
                          {evaluation.average_rating?.toFixed(1) || "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          evaluation.status === "acknowledged" ? "bg-green-100 text-green-700" :
                          evaluation.status === "sent_for_signature" ? "bg-amber-100 text-amber-700" :
                          "bg-gray-100 text-gray-600"
                        }`}>
                          {evaluation.status?.replace(/_/g, " ") || "draft"}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-500">{evaluation.evaluator_name}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
